/**
 * Turning a column name and a stored value into something a person can read
 * (docs/11 F-1).
 *
 * Audit field diffs print the column and the raw value. Unaided that produced
 * lines like `coi_on_file: false → true` and
 * `est_monthly_cost_cents: 0 → 145000` in the history of a real vendor.
 *
 * Deliberately NOT an exhaustive map of every column in the schema: that map
 * would be 300 lines, would drift the moment a migration lands, and cannot be
 * gated (a column is not required to ever appear in a diff). Instead:
 *
 *  - a deterministic fallback that gets the ordinary case right —
 *    `valid_until` → "Valid until";
 *  - an override map for the ones the fallback gets WRONG, which is the small
 *    set: acronyms it would lowercase, `_id` it would expose, and `_cents` it
 *    would leave in the label;
 *  - value formatting keyed on the column's SUFFIX, so every `*_cents` column
 *    reads as money without being listed.
 */

import { DOCUMENT_ACCESS_MODE_LABEL } from './contracts/documents.ts';
import { formatCents } from './money.ts';

/** Where the fallback is wrong. Only these — the fallback handles the rest. */
const OVERRIDES: Record<string, string> = {
  coi_on_file: 'COI on file',
  coi_expiration: 'COI expiration',
  w9_on_file: 'W-9 on file',
  tkc_number: 'TKC number',
  tkc_work_url: 'TKC Work URL',
  yardi_id: 'Yardi ID',
  entity_id: 'Ownership entity',
  sq_ft: 'Square feet',
  sq_footage: 'Square feet',
  est_monthly_cost_cents: 'Estimated monthly cost',
  base_rent_cents: 'Base rent',
  labor_rate_per_hour_cents: 'Labor rate (per hour)',
  overtime_rate_per_hour_cents: 'Overtime rate (per hour)',
  trip_charge_cents: 'Trip charge',
  management_fee_per_sq_ft_per_year_cents: 'Management fee (per sq ft / year)',
  doc_type_id: 'Document type',
  type_id: 'Type',
  management_type_id: 'Management type',
  utility_account_type_id: 'Utility account type',
  service_category_id: 'Service',
  group_id: 'Group',
  primary_contact_id: 'Primary contact',
  is_primary: 'Primary',
  is_open_24h: 'Open 24 hours',
  resource_url: 'Document link',
  verify_status: 'Link check',
  critical_information: 'Important details',
  inactivated_reason: 'Reason',
  relationship_type: 'Role on the record',
  superseded_by: 'Replaced by',
  access_mode: 'Who can change it',
  address_line1: 'Address',
  address_line2: 'Address line 2',
  postal_code: 'ZIP',
};

/**
 * camelCase → snake_case, so one override map serves both shapes.
 *
 * This is not tidiness: the ledger genuinely holds both. Most modules key their
 * `field_diffs` on the camelCase FIELD (`officePhone`) and some on the COLUMN
 * (`office_phone`), and years of rows already exist either way — a labeller
 * that only understood one would print "OfficePhone" on real history. New code
 * should key on the column (docs/11 D-1); this stays total regardless.
 */
function normalizeKey(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

/**
 * `some_column_name` → "Some column name". Trailing `_id` is dropped rather
 * than printed, because "Primary contact id" is a database fact and "Primary
 * contact" is the one the reader means.
 */
export function fieldLabel(column: string): string {
  const key = normalizeKey(column);
  const override = OVERRIDES[key];
  if (override !== undefined) return override;
  const words = key
    .replace(/_id$/, '')
    .replace(/_cents$/, '')
    .split('_')
    .filter((part) => part !== '');
  if (words.length === 0) return column;
  const [first, ...rest] = words as [string, ...string[]];
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/**
 * Columns whose stored value is an INTERNAL ENUM, mapped through the domain's
 * own label map rather than a second copy of it (punch 5.3's rule, punch 6.1a's
 * correction). History is a user-visible surface: a line reading
 * `Access mode: organization_read → organization_edit` puts the storage
 * vocabulary in front of a property manager, and if the two labels ever diverge
 * the history and the form are describing the same change differently.
 *
 * Keyed by COLUMN, and only for enums whose values are meaningless to a reader.
 * Statuses are deliberately absent: `STATUSES` is a component seam (a Chip),
 * and the audit lines that carry a status already read as words.
 */
const VALUE_LABELS: Record<string, Record<string, string>> = {
  access_mode: DOCUMENT_ACCESS_MODE_LABEL,
};

/**
 * A stored value as the reader should see it. Money by column suffix so no
 * `*_cents` column has to be listed; booleans as Yes/No because `false` in a
 * history line reads as a failure rather than as an answer; absence as an em
 * dash, which is what every other surface in the app uses.
 */
export function auditValue(column: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (normalizeKey(column).includes('_cents') && typeof value === 'number') {
    return formatCents(value);
  }
  if (typeof value === 'string') {
    const label = VALUE_LABELS[normalizeKey(column)]?.[value];
    if (label !== undefined) return label;
  }
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.length === 0 ? '—' : `${String(value.length)} items`;
  return JSON.stringify(value);
}
